import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import ConfirmationModal from '@/components/ConfirmationModal';
import {
  Shield, ShieldCheck, ShieldAlert, RefreshCw, Plus, ChevronRight,
  ArrowLeft, AlertTriangle, CheckCircle, XCircle, MinusCircle, Eye,
} from 'lucide-react';

const getCsrfToken = () => document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/)?.[1] ?? '';
const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json',
  'X-CSRF-Token': getCsrfToken(),
});

const STATUS_COLORS: Record<string, string> = {
  compliant: 'bg-green-100 text-green-800',
  partially_compliant: 'bg-amber-100 text-amber-800',
  non_compliant: 'bg-red-100 text-red-800',
  not_applicable: 'bg-gray-100 text-gray-600',
};

const STATUS_LABELS: Record<string, string> = {
  compliant: 'Compliant',
  partially_compliant: 'Partially Compliant',
  non_compliant: 'Non-Compliant',
  not_applicable: 'N/A',
};

const AREA_ICONS: Record<string, React.ReactNode> = {
  'Firewalls': <Shield size={20} />,
  'Secure Configuration': <ShieldCheck size={20} />,
  'Access Control': <ShieldAlert size={20} />,
  'Malware Protection': <AlertTriangle size={20} />,
  'Patch Management': <RefreshCw size={20} />,
};

interface Assessment {
  id: number;
  name: string;
  status: string;
  overall_score: number;
  created_at: string;
  updated_at: string;
}

interface Control {
  id: number;
  control_id: string;
  name: string;
  area: string;
  status: string;
  evidence_link?: string;
  remediation_notes?: string;
}

interface AreaBreakdown {
  area: string;
  score: number;
  total: number;
  compliant: number;
  non_compliant: number;
  partially_compliant: number;
  not_applicable: number;
}

interface Dashboard {
  overall_score: number;
  areas: AreaBreakdown[];
  gaps: Control[];
}

type View = 'dashboard' | 'detail';

const CyberEssentials: React.FC = () => {
  const { user } = useAuth();
  const [view, setView] = useState<View>('dashboard');
  const [dashboard, setDashboard] = useState<Dashboard | null>(null);
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [selectedAssessment, setSelectedAssessment] = useState<Assessment | null>(null);
  const [controls, setControls] = useState<Control[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [createModal, setCreateModal] = useState(false);
  const [createName, setCreateName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editControl, setEditControl] = useState<Control | null>(null);
  const [editStatus, setEditStatus] = useState('');
  const [editEvidence, setEditEvidence] = useState('');
  const [editRemediation, setEditRemediation] = useState('');

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [dashRes, assessRes] = await Promise.all([
        fetch('/api/compliance/cyber-essentials/readiness-dashboard', { headers: authHeaders() }),
        fetch('/api/compliance/cyber-essentials/assessments', { headers: authHeaders() }),
      ]);
      if (!dashRes.ok) {
        setDashboard({ overall_score: 0, areas: [], gaps: [] });
        setAssessments([]);
        if (dashRes.status !== 404) setError('Failed to load dashboard');
        setLoading(false);
        return;
      }
      if (!assessRes.ok) {
        setAssessments([]);
        if (assessRes.status !== 404) setError('Failed to load assessments');
      } else {
        const assessData = await assessRes.json();
        const list = assessData?.assessments ?? assessData;
        setAssessments(Array.isArray(list) ? list : []);
      }
      const dashData = await dashRes.json();
      setDashboard({
        overall_score: dashData?.overall_score ?? 0,
        areas: Array.isArray(dashData?.areas) ? dashData.areas : [],
        gaps: Array.isArray(dashData?.gaps) ? dashData.gaps : [],
      });
    } catch (e: any) {
      setError(e.message || 'Failed to load data');
      setDashboard({ overall_score: 0, areas: [], gaps: [] });
      setAssessments([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  const openAssessment = async (assessment: Assessment) => {
    setLoading(true);
    try {
      const res = await fetch(`/api/compliance/cyber-essentials/assessments/${assessment.id}`, { headers: authHeaders() });
      if (!res.ok) {
        setControls([]);
        if (res.status !== 404) setError('Failed to load assessment');
        return;
      }
      const data = await res.json();
      const ctrlList = data?.controls ?? data?.assessment?.controls;
      setSelectedAssessment(assessment);
      setControls(Array.isArray(ctrlList) ? ctrlList : []);
      setView('detail');
    } catch (e: any) {
      setError(e.message || 'Failed to load assessment');
      setControls([]);
    } finally {
      setLoading(false);
    }
  };

  const createAssessment = async () => {
    if (!createName.trim()) return;
    setSubmitting(true);
    try {
      const res = await fetch('/api/compliance/cyber-essentials/assessments', {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ name: createName }),
      });
      if (!res.ok) throw new Error('Failed to create assessment');
      setCreateModal(false);
      setCreateName('');
      loadDashboard();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const updateControl = async () => {
    if (!editControl) return;
    setSubmitting(true);
    try {
      const res = await fetch(`/api/compliance/cyber-essentials/controls/${editControl.id}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({
          status: editStatus,
          evidence_link: editEvidence,
          remediation_notes: editRemediation,
        }),
      });
      if (!res.ok) throw new Error('Failed to update control');
      setControls(prev => prev.map(c =>
        c.id === editControl.id
          ? { ...c, status: editStatus, evidence_link: editEvidence, remediation_notes: editRemediation }
          : c
      ));
      setEditControl(null);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const openEditControl = (ctrl: Control) => {
    setEditControl(ctrl);
    setEditStatus(ctrl.status);
    setEditEvidence(ctrl.evidence_link || '');
    setEditRemediation(ctrl.remediation_notes || '');
  };

  const scoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 50) return 'text-amber-600';
    return 'text-red-600';
  };

  const scoreRingColor = (score: number) => {
    if (score >= 80) return 'stroke-green-500';
    if (score >= 50) return 'stroke-amber-500';
    return 'stroke-red-500';
  };

  const groupedControls = controls.reduce<Record<string, Control[]>>((acc, ctrl) => {
    const area = ctrl.area || 'Other';
    if (!acc[area]) acc[area] = [];
    acc[area].push(ctrl);
    return acc;
  }, {});

  const gapControls = controls.filter(c => c.status === 'non_compliant');

  if (loading && !dashboard) {
    return (
      <div className="p-6 flex items-center justify-center h-64">
        <RefreshCw className="animate-spin text-blue-600" size={32} />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          {view === 'detail' && (
            <button onClick={() => { setView('dashboard'); setSelectedAssessment(null); }} className="p-2 hover:bg-gray-100 rounded-lg">
              <ArrowLeft size={20} />
            </button>
          )}
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {view === 'detail' ? selectedAssessment?.name : 'Cyber Essentials Readiness'}
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              {view === 'detail' ? 'Assessment Controls' : 'Track your Cyber Essentials compliance across all 5 control areas'}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={loadDashboard} className="flex items-center gap-1 px-3 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50">
            <RefreshCw size={16} />
          </button>
          {view === 'dashboard' && (
            <button onClick={() => setCreateModal(true)} className="flex items-center gap-1 px-3 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700">
              <Plus size={16} /> New Assessment
            </button>
          )}
        </div>
      </div>

      {error && <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{error}</div>}

      {view === 'dashboard' && dashboard && (
        <>
          {/* Overall Score + Area Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-6 gap-6">
            {/* Score Ring */}
            <div className="lg:col-span-1 bg-white border rounded-xl p-6 flex flex-col items-center justify-center">
              <div className="relative w-28 h-28">
                <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
                  <circle cx="18" cy="18" r="15.5" fill="none" stroke="#e5e7eb" strokeWidth="3" />
                  <circle
                    cx="18" cy="18" r="15.5" fill="none"
                    className={scoreRingColor(dashboard.overall_score)}
                    strokeWidth="3"
                    strokeDasharray={`${dashboard.overall_score} ${100 - dashboard.overall_score}`}
                    strokeLinecap="round"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className={`text-2xl font-bold ${scoreColor(dashboard.overall_score)}`}>
                    {Math.round(dashboard.overall_score)}%
                  </span>
                </div>
              </div>
              <p className="text-sm text-gray-500 mt-2">Overall Score</p>
            </div>

            {/* Area Cards */}
            {(dashboard.areas || []).map(area => (
              <div key={area.area} className="bg-white border rounded-xl p-4">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-blue-600">{AREA_ICONS[area.area] || <Shield size={20} />}</span>
                  <h3 className="text-sm font-semibold text-gray-900 truncate">{area.area}</h3>
                </div>
                <div className={`text-2xl font-bold ${scoreColor(area.score)}`}>
                  {Math.round(area.score)}%
                </div>
                <div className="mt-2 flex gap-1 text-xs">
                  <span className="text-green-600">{area.compliant} ok</span>
                  <span className="text-gray-400">|</span>
                  <span className="text-red-600">{area.non_compliant} gap</span>
                </div>
              </div>
            ))}
          </div>

          {/* Gap Summary */}
          {dashboard.gaps && dashboard.gaps.length > 0 && (
            <div className="bg-white border rounded-xl p-5">
              <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <XCircle size={20} className="text-red-500" /> Gap Summary ({dashboard.gaps.length} non-compliant)
              </h2>
              <div className="divide-y">
                {dashboard.gaps.map((gap, i) => (
                  <div key={i} className="py-2 flex items-center justify-between">
                    <div>
                      <span className="text-sm font-mono text-gray-500 mr-2">{gap.control_id}</span>
                      <span className="text-sm text-gray-900">{gap.name}</span>
                      <span className="ml-2 text-xs text-gray-400">{gap.area}</span>
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS.non_compliant}`}>
                      Non-Compliant
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Assessment List */}
          <div className="bg-white border rounded-xl p-5">
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Assessments</h2>
            {assessments.length === 0 ? (
              <p className="text-sm text-gray-500 py-4 text-center">No assessments yet. Create one to get started.</p>
            ) : (
              <div className="divide-y">
                {assessments.map(a => (
                  <div
                    key={a.id}
                    onClick={() => openAssessment(a)}
                    className="py-3 flex items-center justify-between cursor-pointer hover:bg-gray-50 px-2 rounded-lg"
                  >
                    <div>
                      <p className="text-sm font-medium text-gray-900">{a.name}</p>
                      <p className="text-xs text-gray-500">Created {new Date(a.created_at).toLocaleDateString()}</p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`text-sm font-semibold ${scoreColor(a.overall_score || 0)}`}>
                        {Math.round(a.overall_score || 0)}%
                      </span>
                      <ChevronRight size={16} className="text-gray-400" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* Assessment Detail View */}
      {view === 'detail' && (
        <>
          {/* Gap summary for this assessment */}
          {gapControls.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <h3 className="text-sm font-semibold text-red-800 mb-2 flex items-center gap-2">
                <AlertTriangle size={16} /> {gapControls.length} Non-Compliant Control{gapControls.length > 1 ? 's' : ''}
              </h3>
              <div className="flex flex-wrap gap-2">
                {gapControls.map(c => (
                  <span key={c.id} className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded font-mono">{c.control_id}</span>
                ))}
              </div>
            </div>
          )}

          {/* Controls grouped by area */}
          {Object.entries(groupedControls).map(([area, areaControls]) => (
            <div key={area} className="bg-white border rounded-xl overflow-hidden">
              <div className="bg-gray-50 px-5 py-3 border-b flex items-center gap-2">
                <span className="text-blue-600">{AREA_ICONS[area] || <Shield size={18} />}</span>
                <h3 className="text-sm font-semibold text-gray-900">{area}</h3>
                <span className="ml-auto text-xs text-gray-500">{areaControls.length} controls</span>
              </div>
              <div className="divide-y">
                {areaControls.map(ctrl => (
                  <div key={ctrl.id} className="px-5 py-3 flex items-center gap-4">
                    <span className="text-xs font-mono text-gray-500 w-16 shrink-0">{ctrl.control_id}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-900">{ctrl.name}</p>
                      {ctrl.evidence_link && (
                        <a href={ctrl.evidence_link} target="_blank" rel="noopener noreferrer"
                          className="text-xs text-blue-600 hover:underline flex items-center gap-1 mt-0.5">
                          <Eye size={12} /> Evidence
                        </a>
                      )}
                      {ctrl.remediation_notes && (
                        <p className="text-xs text-gray-500 mt-0.5 truncate">{ctrl.remediation_notes}</p>
                      )}
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ${STATUS_COLORS[ctrl.status] || STATUS_COLORS.not_applicable}`}>
                      {STATUS_LABELS[ctrl.status] || ctrl.status}
                    </span>
                    <button
                      onClick={() => openEditControl(ctrl)}
                      className="text-xs text-blue-600 hover:text-blue-800 whitespace-nowrap"
                    >
                      Update
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </>
      )}

      {/* Create Assessment Modal */}
      {createModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">New Assessment</h2>
            <label className="block text-sm font-medium text-gray-700 mb-1">Assessment Name</label>
            <input
              type="text"
              value={createName}
              onChange={e => setCreateName(e.target.value)}
              placeholder="e.g. Q1 2026 Cyber Essentials Review"
              className="w-full border rounded-lg px-3 py-2 text-sm mb-4"
            />
            <div className="flex justify-end gap-2">
              <button onClick={() => setCreateModal(false)} className="px-4 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50">
                Cancel
              </button>
              <button
                onClick={createAssessment}
                disabled={submitting || !createName.trim()}
                className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {submitting ? 'Creating...' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Control Modal */}
      {editControl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Update Control</h2>
            <p className="text-sm text-gray-500 mb-4">{editControl.control_id} - {editControl.name}</p>

            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={editStatus}
              onChange={e => setEditStatus(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 text-sm mb-3"
            >
              <option value="compliant">Compliant</option>
              <option value="partially_compliant">Partially Compliant</option>
              <option value="non_compliant">Non-Compliant</option>
              <option value="not_applicable">Not Applicable</option>
            </select>

            <label className="block text-sm font-medium text-gray-700 mb-1">Evidence Link</label>
            <input
              type="text"
              value={editEvidence}
              onChange={e => setEditEvidence(e.target.value)}
              placeholder="https://..."
              className="w-full border rounded-lg px-3 py-2 text-sm mb-3"
            />

            <label className="block text-sm font-medium text-gray-700 mb-1">Remediation Notes</label>
            <textarea
              value={editRemediation}
              onChange={e => setEditRemediation(e.target.value)}
              rows={3}
              className="w-full border rounded-lg px-3 py-2 text-sm mb-4"
            />

            <div className="flex justify-end gap-2">
              <button onClick={() => setEditControl(null)} className="px-4 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50">
                Cancel
              </button>
              <button
                onClick={updateControl}
                disabled={submitting}
                className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {submitting ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CyberEssentials;
